import { LaptopIcon, MaterialIcon, VehicleMaterialIcon } from '@schuettflix/icons-react';
import { cn } from '@/shared/utils/cn.ts';
import { PRICING_STRATEGY, PRODUCT_TYPES, SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { getProductCategoryByProductType } from '@schuettflix/product-group-utils';
import { useTranslation } from 'react-i18next';
import { DISPOSAL_PURPOSE_LABEL, PRODUCT_CARD_NAME_LABEL, TRANSPORT_UNIT_LABEL } from '../utils/productCardLabelMaps';
import { ProductTemplateSelectedOption } from '@/shared/components/PresetSelection/types.ts';

interface StandaloneProductCardProps {
    productType: PRODUCT_TYPES;
    pricingStrategy: PRICING_STRATEGY;
    disposalPurpose?: 'RECYCLING' | 'DISPOSAL';
    unit?: SUPPORTED_UNITS;
    onSelect: (option: ProductTemplateSelectedOption) => void;
}

function useCardLabels(
    productType: PRODUCT_TYPES,
    unit: SUPPORTED_UNITS | undefined,
    disposalPurpose: 'RECYCLING' | 'DISPOSAL' | undefined
) {
    const { t } = useTranslation();

    if (unit && ['CUSTOM_TRANSPORT', 'TRANSPORT'].includes(productType)) {
        return {
            title: t(TRANSPORT_UNIT_LABEL[unit]),
            description: t(PRODUCT_CARD_NAME_LABEL[productType]),
        };
    } else {
        return {
            title: t(PRODUCT_CARD_NAME_LABEL[productType]),
            description: disposalPurpose ? t(DISPOSAL_PURPOSE_LABEL[disposalPurpose]) : undefined,
        };
    }
}

export const StandaloneProductCard = ({
    productType,
    pricingStrategy,
    disposalPurpose,
    unit,
    onSelect,
}: StandaloneProductCardProps) => {
    const { title, description } = useCardLabels(productType, unit, disposalPurpose);

    return (
        <div
            className={cn('shadow-low flex basis-1/4 cursor-pointer rounded-md bg-white p-4', {
                'h-[52px]': !description,
                'h-[76px]': description,
            })}
            onClick={() =>
                onSelect({
                    productType,
                    pricingStrategy,
                    isStandalone: true,
                    formPreset: {
                        billingUnit: unit,
                        disposalPurpose,
                    },
                })
            }
            data-test={unit ? `standaloneProductCard-${productType}-${unit}` : `standaloneProductCard-${productType}`}
        >
            <div className="flex w-full flex-col items-start justify-center gap-1">
                {description && <span className="text-subdued font-copy-md">{description}</span>}
                <span
                    className={cn('text font-copy-md-strong', {
                        'flex w-full justify-between': !description,
                    })}
                >
                    <span>{title}</span>
                    {!description && <ProductCategoryIcon productType={productType} />}
                </span>
            </div>

            {description && (
                <div className="flex w-1/2 flex-col items-end justify-start">
                    <ProductCategoryIcon productType={productType} />
                </div>
            )}
        </div>
    );
};

const ProductCategoryIcon = ({ productType }: { productType: PRODUCT_TYPES }) => {
    const productCategory = getProductCategoryByProductType(productType);

    switch (productCategory) {
        case 'TRANSPORT':
            return <VehicleMaterialIcon size="xs" />;
        case 'MATERIAL':
            return <MaterialIcon size="xs" />;
        case 'MATERIAL_SERVICE':
        case 'TRANSPORT_SERVICE':
            return <LaptopIcon size="xs" />;
    }
};
